import { useState, useEffect } from 'react';
import './App.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const API_KEY = '43218602';

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [series, setSeries] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const CustomDot = (props) => {
    const { cx, cy, value } = props;
    if (value === null || value === undefined) return null;

    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="#61dafb"
        stroke="none"
      />
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;

    const data = payload[0].payload;

    return (
      <div
        style={{
          backgroundColor: '#282c34',
          border: '1px solid #61dafb',
          padding: isMobile ? '6px 8px' : '8px 10px',
          borderRadius: '4px',
          fontSize: isMobile ? '9px' : '11px',
          maxWidth: isMobile ? '140px' : '180px'
        }}
      >
        <div style={{ marginBottom: '3px' }}>
          <span style={{ color: '#61dafb', fontWeight: 'bold' }}>S{data.saison}E{data.numero}</span>
        </div>
        <div style={{ marginBottom: '3px', fontSize: isMobile ? '8px' : '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <span style={{ color: '#ffffff' }}>{data.titre}</span>
        </div>
        <div style={{ marginBottom: '2px' }}>
          <span style={{ color: '#61dafb' }}>★ </span>
          <span style={{ color: '#ffffff' }}>{data.note ? data.note.toFixed(1) : 'N/A'}</span>
        </div>
        <div style={{ fontSize: isMobile ? '8px' : '9px', color: '#888' }}>
          {data.date}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const searchSeries = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setLoading(true);
    setError('');
    setEpisodes([]);
    setSeries(null);

    try {
      // Fetch series info
      const seriesResponse = await fetch(
        `http://www.omdbapi.com/?t=${encodeURIComponent(searchTerm)}&type=series&apikey=${API_KEY}`
      );
      const seriesData = await seriesResponse.json();

      if (seriesData.Response === 'False') {
        setError(seriesData.Error || 'Série non trouvée');
        setLoading(false);
        return;
      }

      setSeries(seriesData);

      // Fetch all episodes from all seasons
      const totalSeasons = parseInt(seriesData.totalSeasons);
      const allEpisodes = [];

      for (let season = 1; season <= totalSeasons; season++) {
        const seasonResponse = await fetch(
          `http://www.omdbapi.com/?t=${encodeURIComponent(searchTerm)}&Season=${season}&apikey=${API_KEY}`
        );
        const seasonData = await seasonResponse.json();

        if (seasonData.Response === 'True' && seasonData.Episodes) {
          seasonData.Episodes.forEach(episode => {
            allEpisodes.push({
              ...episode,
              Season: season
            });
          });
        }
      }

      setEpisodes(allEpisodes);
    } catch (err) {
      setError('Erreur lors de la récupération des données');
    }

    setLoading(false);
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>TV Series Episode Viewer</h1>
        <form onSubmit={searchSeries}>
          <input
            type="text"
            placeholder="Entrez le nom d'une série..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Rechercher</button>
        </form>
      </header>

      <main className="App-main">
        {loading && <div className="loading">Chargement...</div>}

        {error && <div className="error">{error}</div>}

        {series && !loading && (
          <div className="series-info">
            <h2>{series.Title} ({series.Year})</h2>
            {episodes.length > 0 && (
              <div className="season-filter">
                <button
                  className={selectedSeason === 'all' ? 'season-button active' : 'season-button'}
                  onClick={() => setSelectedSeason('all')}
                >
                  Toutes les saisons
                </button>
                {Array.from({ length: parseInt(series.totalSeasons) }, (_, i) => i + 1).map(season => (
                  <button
                    key={season}
                    className={selectedSeason === season ? 'season-button active' : 'season-button'}
                    onClick={() => setSelectedSeason(season)}
                  >
                    Saison {season}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {episodes.length > 0 && !loading && (() => {
          const filteredEpisodes = episodes.filter(ep => selectedSeason === 'all' || ep.Season === selectedSeason);
          return (
          <>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={isMobile ? 350 : 600}>
                <LineChart
                  data={filteredEpisodes.map((ep, idx) => ({
                  name: `S${ep.Season}E${ep.Episode}`,
                  episodeLabel: ep.Episode,
                  episode: idx,
                  uniqueId: idx,
                  note: ep.imdbRating !== 'N/A' ? parseFloat(ep.imdbRating) : null,
                  titre: ep.Title,
                  saison: ep.Season,
                  numero: ep.Episode,
                  date: ep.Released
                }))}
                margin={isMobile
                  ? { top: 20, right: 10, left: 5, bottom: 40 }
                  : { top: 40, right: 30, left: 20, bottom: 80 }
                }
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis
                  dataKey="uniqueId"
                  stroke="#61dafb"
                  interval={isMobile ? 'preserveStartEnd' : 0}
                  angle={isMobile ? -60 : -45}
                  textAnchor="end"
                  height={isMobile ? 50 : 80}
                  tick={{ fontSize: isMobile ? 8 : 10 }}
                  tickFormatter={(value) => filteredEpisodes[value] ? filteredEpisodes[value].Episode : value}
                />
                <YAxis
                  domain={[0, 10]}
                  ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                  stroke="#61dafb"
                  tick={{ fontSize: isMobile ? 8 : 10 }}
                  width={isMobile ? 25 : 40}
                  label={{ value: '', angle: -90, position: 'insideLeft', fill: '#61dafb' }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: '#61dafb', strokeWidth: 0.5, style: { cursor: 'pointer' } }}
                />
                {selectedSeason === 'all' && filteredEpisodes.map((ep, idx) => {
                  if (idx === 0) return null;
                  if (ep.Season !== filteredEpisodes[idx - 1].Season) {
                    return (
                      <ReferenceLine
                        key={`season-${idx}`}
                        x={idx}
                        stroke="#ffcc00"
                        strokeWidth={isMobile ? 1 : 2}
                        strokeOpacity={0.8}
                        label={{
                          value: isMobile ? `${ep.Season}` : `Saison ${ep.Season}`,
                          position: 'top',
                          fill: '#ffcc00',
                          fontSize: isMobile ? 10 : 16,
                          fontWeight: 'bold',
                          offset: isMobile ? 5 : 10
                        }}
                      />
                    );
                  }
                  return null;
                })}
                <Line
                  type="monotone"
                  dataKey="note"
                  stroke="#61dafb"
                  strokeWidth={isMobile ? 1.5 : 2}
                  dot={isMobile ? false : <CustomDot />}
                  activeDot={{ r: isMobile ? 4 : 6 }}
                  connectNulls
                  name="Note IMDB"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          </>
          );
        })()}

      </main>
    </div>
  );
}

export default App;
